/**
 * Групи інтерфейсу — одне місце, де вирішується, який вигляд бачить акаунт.
 *
 * Раніше вигляд складався з кількох незалежних списків логінів (компактне меню
 * ArtemK, портфельна головна, меню Табаків, меню технічного диспетчера). Вони
 * розповзались, і акаунт легко випадав з однієї з перевірок — саме так OlyaK
 * лишилась на старій головній, коли решту вже перевели.
 *
 * Тепер акаунт належить рівно ОДНІЙ групі, а група задає весь вигляд: меню,
 * головну сторінку й дрібні відмінності в компонуванні. Додати людину —
 * один рядок у MEMBERS; завести новий вигляд — одна група.
 *
 * Групу можна визначити й роллю: нові акаунти з роллю, якій уже відповідає
 * група, отримують сучасний вигляд самі, без правки коду.
 */

export type ViewGroup =
  /** Компактний вигляд власника: своє меню з 6 пунктів, ряд розділів, вужча верстка. */
  | 'compact'
  /** Портфельний: сучасна головна з плитками СЕС + повне меню. */
  | 'portfolio'
  /** Технічна зміна: головна з плитками + лише Дашборд і SCADA. */
  | 'scada'
  /** Одна станція: вузьке меню під конкретну СЕС. */
  | 'station'
  /** Класичний: стара головна з віджетами + повне меню. */
  | 'classic';

export interface ViewUser {
  login?: string | null;
  role?: string | null;
}

/**
 * Явна належність за логіном. Порядок перевірки — зверху вниз, акаунт
 * потрапляє в першу групу, де згаданий.
 */
export const MEMBERS: Record<Exclude<ViewGroup, 'classic'>, readonly string[]> = {
  // Артем і дзеркала його кабінету.
  compact: ['ArtemK', 'sergiyt'],
  // Одна станція — акаунт Табаків.
  station: ['tabaki'],
  // Технічний диспетчер.
  scada: ['dispatch'],
  // Решта людей, які вже працюють із сучасною головною: комерційні диспетчери,
  // оператори, аналітик і власники. Власники бачать лише свої СЕС — перелік
  // станцій ріже scope із токена, а не ця група.
  portfolio: [
    'OksanaO', 'unonazv', 'ihork', 'olyap', 'Viktor', 'radmirt', 'IhorA',
    'Elena', 'Yurii', 'OlyaK',
  ],
};

/**
 * Ролі, які потрапляють у групу автоматично. Завдяки цьому новий акаунт із
 * відомою роллю не треба дописувати в MEMBERS — він одразу бачить сучасний
 * вигляд, а не стару головну.
 */
const ROLE_GROUP: Record<string, ViewGroup> = {
  'CTO': 'portfolio',          // VolodP і сервісний helios-digest
  'Оператор': 'portfolio',
  'Аналітик': 'portfolio',
  'Фінансовий директор': 'portfolio',
};

export function viewGroupOf(user: ViewUser | null | undefined): ViewGroup {
  if (!user) return 'classic';
  const login = user.login ?? '';
  for (const g of ['compact', 'station', 'scada', 'portfolio'] as const) {
    if (login && MEMBERS[g].includes(login)) return g;
  }
  return (user.role && ROLE_GROUP[user.role]) || 'classic';
}

/** Чи бачить акаунт сучасну головну з плитками СЕС. */
export function hasPortfolioHome(user: ViewUser | null | undefined): boolean {
  const g = viewGroupOf(user);
  return g === 'portfolio' || g === 'compact' || g === 'scada' || g === 'station';
}

/**
 * Компактний вигляд (історично «як у ArtemK»): власне меню, інша верстка
 * дашборда, ряд розділів «Оптимізації», SCADA без зайвих обгорток.
 * Назва функції лишилась незмінною — її викликають дев'ять місць.
 */
export function hasArtemKView(login: string | null | undefined): boolean {
  return viewGroupOf({ login }) === 'compact';
}

/**
 * Які маршрути ДОЗВОЛЕНІ групі. Меню лише ховає пункти, а пряма адреса
 * відкривала будь-яку сторінку: акаунт dispatch, якому готували «Дашборд і
 * SCADA», бачив увесь кабінет. Тепер DashboardLayout перевіряє шлях і
 * повертає на головну. Групи без запису — без обмежень.
 */
const ALLOWED_PREFIXES: Partial<Record<ViewGroup, readonly string[]>> = {
  scada: ['/', '/scada'],
};

export function isPathAllowedFor(user: ViewUser | null | undefined, pathname: string): boolean {
  const allowed = ALLOWED_PREFIXES[viewGroupOf(user)];
  if (!allowed) return true;
  return allowed.some((p) => (p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/')));
}

/**
 * Примусовий перелогін: якщо для логіна тут новіша «епоха», ніж збережена в
 * браузері при вході, збережену сесію скидаємо, і людина заходить заново —
 * уже з оновленим виглядом. Змінити значення = викинути всіх із цим логіном
 * один раз. Порівняння логіна без урахування регістру.
 */
export const SESSION_EPOCH: Record<string, string> = {
  dispatch: '2026-10-16a',
};
export function sessionEpochFor(login: string | null | undefined): string | undefined {
  if (!login) return undefined;
  const k = Object.keys(SESSION_EPOCH).find((x) => x.toLowerCase() === login.toLowerCase());
  return k ? SESSION_EPOCH[k] : undefined;
}
